/** Service Desk console: Service settings, part B - SLA & calendars, Routing, Macros, Client portal, Notifications & timers, CSAT (SD-S1-47). */
export const serviceSettingsB = {
  nav: {
    sla: "SLA & calendars",
    routing: "Routing",
    macros: "Macros",
    portal: "Client portal",
    notifications: "Notifications & timers",
    csat: "CSAT",
  },
  bkit: {
    save: "Save changes",
    discard: "Discard",
    cancel: "Cancel",
    unsaved: "You have unsaved changes",
    list: {
      add: "Add",
      empty: "Type something to add first.",
      none: "Nothing added yet.",
      count: "{{count}} of {{max}}",
      remove: "Remove {{item}}",
      duplicate: "“{{item}}” is already in the list.",
      full: "The list is full: {{max}} at most.",
      added: "Added {{item}}.",
      removed: "Removed {{item}}.",
    },
    errors: {
      plan: {
        policies: "Your plan has no room for another SLA policy. Upgrade to add more.",
        calendars: "Business calendars come with the Pro plan. Upgrade to add one.",
        rules: "Your plan has no room for another routing rule. Upgrade to add more.",
        skills: "Skills routing comes with the Pro plan.",
        macros: "Your plan has no room for another macro. Upgrade to add more.",
      },
      calendarInUse_one: "{{count}} SLA policy still counts its targets in this calendar. Move it to another calendar first.",
      calendarInUse_other: "{{count}} SLA policies still count their targets in this calendar. Move them to another calendar first.",
      policyInUse: "Ticket types or the workspace default still use this policy. Choose another policy for them first.",
    },
    workflows: {
      open: "Open the workflow",
      paused: "“{{name}}” is paused, so nothing uses these settings until it is resumed.",
      pausedChip: "Paused",
      usedBy: "Used by",
      ticket_unassigned_escalation: "Escalate unassigned tickets",
      ticket_waiting_reminder: "Remind clients of waiting tickets",
      ticket_pending_auto_resolve: "Resolve tickets nobody answers",
      ticket_auto_close: "Close resolved tickets",
      ticket_csat_request: "Ask for a rating when resolved",
      ticket_low_csat_alert: "Alert the team lead on a low rating",
    },
  },
  sla: {
    standardNote: "Standard includes 1 SLA policy and counts its targets around the clock. Several policies, business calendars with holidays and contract SLAs are part of the Pro plan.",
    policies: "SLA policies",
    new: "New policy",
    newTitle: "New SLA policy",
    newHint: "It starts with sample targets (urgent: 1 hour to reply) that you can change right away.",
    create: "Create policy",
    created: "Policy “{{name}}” created.",
    name: "Policy name",
    nameRequired: "Give the policy a name.",
    saved: "Policy “{{name}}” saved.",
    isDefault: "Workspace default",
    makeDefault: "Make default",
    defaultSet: "“{{name}}” is now the workspace default.",
    usedBy_one: "{{count}} ticket type uses it",
    usedBy_other: "{{count}} ticket types use it",
    delete: "Delete",
    deleteTitle: "Delete “{{name}}”?",
    deleteBody: "Tickets already using it keep their due times. A policy that ticket types or the workspace default still use cannot be deleted.",
    deleteConfirm: "Delete policy",
    deleted: "Policy “{{name}}” deleted.",
    emptyTitle: "No SLA policy yet",
    emptyBody: "A policy says how fast a ticket should get its first reply and its resolution, by priority. Without one, tickets show no SLA.",
    matrix: {
      region: "SLA targets",
      caption: "Targets in hours by priority and clock; leave a cell empty for no target",
      priority: "Priority",
      none: "No target",
      cell: "{{priority}}: {{metric}} in hours",
      hint: "Hours, counted in the calendar's open time. Use decimals for minutes (0.5 is 30 minutes). Leave a cell empty for no target.",
      invalid: "Use a number of hours above 0 and up to 8,760 (a year), with at most two decimals.",
      needsFirst: "Set at least one first response target.",
      col: {
        first_response: "First response (h)",
        next_response: "Next response (h)",
        resolution: "Resolution (h)",
        onsite_response: "On-site response (h)",
      },
      metric: {
        first_response: "first response",
        next_response: "next response",
        resolution: "resolution",
        onsite_response: "on-site response",
      },
    },
    calendar: {
      label: "Business calendar",
      hint: "The targets count only the open time of this calendar and skip its holidays.",
      none: "No calendar: count around the clock",
      lockedTitle: "Business calendars come with the Pro plan",
      lockedBody: "Targets on your plan count around the clock. Upgrade to count only office hours and skip holidays.",
      name: "Calendar name",
      nameRequired: "Give the calendar a name.",
      timezone: "Time zone",
      timezoneHint: "Dates, SLA due times and reports use this zone.",
      usedBy_one: "{{count}} SLA policy counts in it",
      usedBy_other: "{{count}} SLA policies count in it",
      save: "Save calendar",
      saved: "Calendar “{{name}}” saved.",
      delete: "Delete calendar",
      deleteTitle: "Delete “{{name}}”?",
      deleteBody: "Its weekly hours and holidays are removed. A calendar that SLA policies still count in cannot be deleted.",
      deleteConfirm: "Delete calendar",
      deleted: "Calendar “{{name}}” deleted.",
      newTitle: "New business calendar",
      newHint: "Set the weekly open hours. Holidays are added next.",
      create: "Create calendar",
      created: "Calendar “{{name}}” created.",
      seedLabel: "Start the holiday list with the Philippine 2026 and 2027 holidays",
      seedHint: "Regular and special non-working days. You can edit every one afterwards.",
    },
    warn: {
      label: "Warn when this share of the target has passed",
      hint: "Chips turn to warning at this share and to danger when time runs out. 75% is the usual choice.",
    },
    preview: {
      title: "Preview a due date",
      priority: "Priority",
      metric: "Clock",
      created: "Ticket created",
      noTarget: "Type hours for this priority and clock in the matrix to see when a ticket would be due.",
      failed: "The preview is not available right now. The matrix is unchanged.",
      moment: {
        friday: "Next Friday 16:30",
        wednesday: "Next Wednesday 09:30",
        beforeHoliday: "The day before {{holiday}}, 16:00",
        now: "Right now",
      },
    },
    calendars: {
      title: "Business calendars",
      intro: "The open hours and holidays that SLA targets count in.",
      new: "New calendar",
      pick: "Calendar",
      emptyTitle: "No business calendar yet",
      emptyBody: "Create one so targets count only the hours your team works and skip holidays.",
      lockedTitle: "Business calendars come with the Pro plan",
      lockedBody: "Targets count around the clock on your plan. Upgrade for weekly hours, a time zone and a holiday list.",
    },
    week: {
      title: "Weekly open hours",
      copyMonday: "Copy Monday to Tue to Fri",
      open: "{{day}} is open",
      closed: "Closed",
      from: "{{day}}: opens at",
      to: "{{day}}: closes at",
      addSpan: "Add hours on {{day}}",
      removeSpan: "Remove these hours on {{day}}",
      neverOpen: "Open at least one day: a calendar that is never open cannot count anything.",
      problem: {
        order: "{{day}}: each opening time must be before its closing time.",
        overlap: "{{day}}: the open hours overlap. Make them one stretch or leave a gap.",
      },
    },
    days: {
      mon: "Monday",
      tue: "Tuesday",
      wed: "Wednesday",
      thu: "Thursday",
      fri: "Friday",
      sat: "Saturday",
      sun: "Sunday",
    },
    holidays: {
      title: "Holidays",
      intro: "Days “{{calendar}}” is closed. The Philippine 2026 and 2027 list can seed it; edit it when a proclamation changes.",
      seed: "Add Philippine 2026-2027",
      seeded_one: "Added {{count}} holiday.",
      seeded_other: "Added {{count}} holidays.",
      emptyTitle: "No holidays",
      emptyBody: "Every day counts as a working day for this calendar. Add the Philippine holidays or enter your own.",
      regular: "Regular",
      special: "Special",
      addTitle: "Add a holiday",
      editing: "Edit holiday",
      date: "Date",
      name: "Name",
      specialLabel: "Special non-working day",
      specialHint: "Regular holidays are fixed by law; special days are proclaimed year by year.",
      add: "Add holiday",
      saveOne: "Save holiday",
      added: "Holiday “{{name}}” added.",
      updated: "Holiday “{{name}}” updated.",
      removed: "Holiday “{{name}}” removed.",
      edit: "Edit {{name}}",
      remove: "Remove {{name}}",
      problem: {
        dateRequired: "Pick a date.",
        nameRequired: "Name the holiday.",
        nameLong: "Use 80 characters or fewer.",
        duplicate: "This date is already a holiday.",
        full: "A calendar can have 150 holidays at most.",
      },
    },
    precedence: {
      title: "Which policy applies",
      asset_contract: "An active contract covering the ticket's equipment: its policy.",
      organisation_contract: "An active contract of the client's organisation: its policy.",
      ticket_type: "The ticket type's own policy.",
      workspace_default: "The workspace default policy.",
      none: "Otherwise the ticket has no SLA.",
    },
  },
  routing: {
    title: "Routing rules",
    new: "Add rule",
    newTitle: "Add routing rule",
    editTitle: "Edit routing rule",
    formHint: "A rule says which tickets it takes, which team gets them and how a person is chosen.",
    create: "Add rule",
    save: "Save rule",
    created: "Rule “{{name}}” added.",
    updated: "Rule “{{name}}” saved.",
    deleted: "Rule “{{name}}” deleted.",
    emptyTitle: "No routing rules yet",
    emptyBody: "Without a rule new tickets wait unassigned. Add a rule per kind of ticket, and one last rule with no conditions for everything else.",
    listLabel: "Routing rules, first match wins",
    footFirst: "The first rule that matches wins.",
    footPlain: "{{count}} rules.",
    footLimited: "{{count}} of {{limit}} rules on your plan.",
    footUnlimited: "{{count}} rules; no limit on your plan.",
    noFallback: "No active rule matches every ticket. Tickets no rule matches stay unassigned: add a last rule with no conditions for everything else.",
    when: "When",
    then: "Then",
    always: "every ticket",
    unknownTeam: "an unknown team",
    off: "Off",
    on: "On",
    activeAria: "Rule {{name}} is on",
    edit: "Edit",
    editAria: "Edit {{name}}",
    deleteAria: "Delete {{name}}",
    editSkillsAria: "Edit skills of {{name}}",
    deleteTitle: "Delete “{{name}}”?",
    deleteBody: "Tickets that were routed by it keep their assignee. New tickets move on to the next rule that matches.",
    deleteConfirm: "Delete rule",
    name: "Rule name",
    nameRequired: "Name the rule.",
    team: "Assign to team",
    teamChoose: "Choose a team",
    teamRequired: "Choose the team that gets these tickets.",
    noTeams: "No team exists yet. Create a team in Settings first.",
    method: "How a person is chosen",
    person: "Person",
    personChoose: "Choose a person",
    personRequired: "Choose the person who gets these tickets.",
    skills: "Required skills",
    skillsHint: "A member needs every skill listed. Lowercase letters, numbers and underscores.",
    skillsPlaceholder: "water_systems",
    skillInvalid: "Start with a letter and use lowercase letters, numbers and underscores (40 characters at most).",
    skillsLockTitle: "Skills matching comes with the Pro plan",
    skillsLockBody: "Round robin, least open and the team queue are included on your plan.",
    priority: "Set the priority to",
    priorityKeep: "Keep the ticket's priority",
    priorityHint: "Optional: a matching ticket gets this priority.",
    setsPriority: "Sets priority {{priority}}",
    tags: "Tags to add",
    tagsHint: "Optional: matching tickets get these tags.",
    tagInvalid: "Use 1 to {{max}} characters.",
    active: "Rule is on",
    activeHint: "A rule that is off is skipped but kept.",
    yes: "Yes",
    no: "No",
    builder: {
      title: "When a ticket matches",
      hint: "All conditions of a group must hold. Add another group to match a different case instead (or).",
      always: "No conditions: this rule matches every ticket. Keep one like it last, for everything else.",
      group: "Group {{n}}",
      or: "or",
      andWord: "and",
      orWord: "or",
      name: "condition {{n}} of group {{group}}",
      factOf: "Fact of {{name}}",
      opOf: "Operator of {{name}}",
      valueOf: "Value of {{name}}",
      valuesOf: "Values of {{name}}",
      remove: "Remove {{name}}",
      addCondition: "Add a condition",
      addAnd: "Add a condition to this group (and)",
      addGroup: "Add another group (or)",
      noValue: "No value needed.",
      choose: "Choose",
      pickOne: "Tick at least one.",
      typeValue: "Type a value.",
      valueInvalid: "Type a short value.",
    },
    facts: {
      ticket_type: "Ticket type",
      ticket_category: "Type category",
      ticket_priority: "Priority",
      ticket_channel: "Channel",
      ticket_origin: "How it was raised",
      ticket_language: "Language",
      ticket_tags: "Tags",
      ticket_keywords: "Words in the title or text",
      ticket_created_in_office_hours: "Raised in office hours",
      ticket_requester_verified: "Client is verified",
      ticket_ai_topic: "Topic found by the assistant",
      ticket_org_tier: "Organisation tier",
      organisation_tier: "Organisation tier (record)",
      organisation_industry: "Organisation industry",
      organisation_tags: "Organisation tags",
      ticket_site_region: "Site region",
      ticket_asset_category: "Equipment category",
      ticket_has_asset: "Has equipment",
      ticket_contract_active: "Has an active contract",
    },
    ops: {
      is: "is",
      is_not: "is not",
      is_any_of: "is any of",
      is_none_of: "is none of",
      contains: "contains",
      not_contains: "does not contain",
      contains_any_of: "contains any of",
      starts_with: "starts with",
      is_set: "is set",
      is_not_set: "is not set",
      gt: "is more than",
      lt: "is less than",
    },
    channels: {
      widget: "Website widget",
      webapp: "Web app",
      mobile: "Mobile app",
      messenger: "Messenger",
      email: "E-mail",
      api: "API",
      console: "Console",
    },
    origins: {
      ai_intake: "Assistant intake",
      client_form: "Client form",
      staff: "Staff",
      email: "E-mail",
      api: "API",
      workflow: "Workflow",
      conversation: "Conversation",
      handoff: "Handoff",
      order_issue: "Order issue",
      visit: "Field visit",
      punch_list: "Punch list",
      pm_plan: "Maintenance plan",
    },
    languages: { en: "English", fil: "Filipino" },
    methods: {
      round_robin: { name: "Round robin", hint: "Take turns between the available members." },
      least_open: { name: "Least open tickets", hint: "The available member with the fewest open tickets." },
      skills_match: { name: "Skills match", hint: "A member who has every required skill; ties go to the least busy." },
      none: { name: "Team queue", hint: "Nobody is named; the ticket waits in the team's queue." },
      user: { name: "A specific person", hint: "Always the same member." },
    },
    test: {
      title: "Test routing",
      intro: "Which rule would take this ticket? Nothing is changed.",
      mode: "What to test",
      sample: "Describe a ticket",
      ticket: "An existing ticket",
      number: "Ticket number",
      numberHint: "For example KHA-10001.",
      numberInvalid: "Use a ticket number like KHA-10001.",
      type: "Ticket type",
      typeRequired: "Choose a ticket type.",
      priority: "Priority",
      channel: "Channel",
      language: "Language",
      hours: "Raised",
      hoursAuto: "Now (the clock decides)",
      hoursIn: "Inside office hours",
      hoursOut: "After hours",
      tags: "Tags",
      tagsHint: "Separate with commas.",
      assetCategory: "Equipment category",
      run: "Test routing",
      matches: "Matches rule {{number}}: {{name}}.",
      goes: "Goes to {{team}} by {{method}}.",
      assignee: "Assigned to {{name}}.",
      none: "No rule matches ({{count}} checked). The ticket stays in the unassigned queue.",
      facts: "Facts the rules looked at",
      notes: {
        team_queue: "Nobody is named: the ticket waits in the team's queue.",
        no_eligible_member: "Nobody in the team can take it now (away or without ticket access), so it waits in the queue.",
        no_skilled_member: "Nobody in the team has all the required skills, so it waits in the queue.",
        at_capacity: "Everyone who could take it is at their capacity cap, so it waits in the queue.",
        user_unavailable: "The person named is away or cannot take tickets, so it waits in the queue.",
      },
    },
    skillsTitle: "Member skills",
    skillsIntro: "The skills match method gives a ticket to a member who has every skill the rule asks for.",
    skillsFailed: "We couldn't load the skills. Try again later.",
    noMembers: "No members to show.",
    noSkills: "No skills",
    skillsDialog: "Skills of {{name}}",
    skillsDialogHint: "Lowercase words such as water_systems or billing.",
    skillsSaved: "Skills of {{name}} saved.",
  },
  macros: {
    new: "New macro",
    newTitle: "New macro",
    editTitle: "Edit macro",
    formHint: "A reply that agents insert in one click, and optionally changes the ticket at the same time.",
    create: "Create macro",
    save: "Save macro",
    saved: "Macro “{{name}}” saved.",
    search: "Search macros",
    searchPlaceholder: "Search macros",
    filter: "Who sees the macro",
    scopes: { all: "All", personal: "Mine", team: "Team", workspace: "Workspace" },
    scopeHint: {
      personal: "Only you see it.",
      team: "Members of the team see it.",
      workspace: "Everyone who works tickets sees it.",
    },
    scope: "Who sees it",
    team: "Team",
    teamRequired: "Choose the team.",
    listLabel: "Macros",
    emptyTitle: "No macros yet",
    emptyBody: "Write the replies you send again and again once, with the client's name filled in for you.",
    noMatchTitle: "No macros match",
    noMatchBody: "Clear the search or the filter, or write a new macro.",
    clear: "Clear search",
    titleLabel: "Name",
    titleRequired: "Name the macro (up to 120 characters).",
    shortcut: "Shortcut",
    shortcutHint: "Optional. Type it after / in the composer, for example /warranty.",
    shortcutInvalid: "Use letters, numbers, / _ and - only (40 at most).",
    bodyEn: "Reply (English)",
    bodyFil: "Reply (Filipino)",
    bodyFilHint: "Optional. Used for clients who write in Filipino; falls back to English.",
    bodyProblem: {
      empty: "Write the reply or add an action.",
      braces: "A variable is missing its closing braces (}}).",
      long: "The reply is too long (10,000 characters at most).",
    },
    noText: "No reply text",
    hasFil: "Filipino",
    noActions: "Reply only",
    used_one: "Used {{count}} time",
    used_other: "Used {{count}} times",
    readOnly: "Shared: managers edit it",
    edit: "Edit",
    editAria: "Edit {{name}}",
    delete: "Delete",
    deleteAria: "Delete {{name}}",
    deleteTitle: "Delete “{{name}}”?",
    deleteBody: "Agents will no longer see it in the composer. Tickets that used it are not changed.",
    deleteConfirm: "Delete macro",
    deleted: "Macro “{{name}}” deleted.",
    variables: {
      title: "Insert a variable",
      insert: "Insert {{variable}}",
      group: {
        ticket: "Ticket",
        contact: "Contact",
        organisation: "Organisation",
        agent: "Agent",
        workspace: "Workspace",
        asset: "Equipment",
        visit: "Visit",
      },
    },
    actions: {
      title: "Then, on the ticket",
      hint: "Actions run when an agent applies the macro. Every one is optional.",
      none: "No actions: the macro only writes the reply.",
      add: "Add an action",
      addButton: "Add action",
      remove: "Remove the action: {{action}}",
      type: {
        set_status: "Set status",
        set_priority: "Set priority",
        set_type: "Change ticket type",
        add_tags: "Add tags",
        remove_tags: "Remove tags",
        assign: "Assign",
        add_note: "Add an internal note",
        set_field: "Set a field",
      },
      label: {
        set_status: "Set status: {{value}}",
        set_priority: "Set priority: {{value}}",
        set_type: "Change type: {{value}}",
        add_tags: "Add tags: {{value}}",
        remove_tags: "Remove tags: {{value}}",
        assign: "Assign to {{value}}",
        add_note: "Add a note",
        set_field: "Set field {{value}}",
      },
      status: "Status",
      statusChoose: "Choose a status",
      code: "Resolution code",
      codeHint: "Optional. A resolved ticket needs a code; this one is used.",
      codeNone: "No code",
      priority: "Priority",
      typeLabel: "Ticket type",
      typeChoose: "Choose a type",
      tagsAdd: "Tags to add",
      tagsRemove: "Tags to remove",
      team: "Team",
      person: "Person",
      leave: "Leave as it is",
      nobody: "Nobody (unassign)",
      note: "Internal note",
      noteHint: "Clients never see it. It can use the same variables as the reply.",
      kept: "This action is kept as it is; edit it in the API.",
      problem: {
        status: "Choose a status.",
        type: "Choose a ticket type.",
        tags: "Add at least one tag.",
        assign: "Choose a team or a person.",
        note: "Write the note.",
      },
    },
    preview: {
      title: "Preview",
      language: "Preview language",
      ticket: "Fill in with a ticket (optional)",
      ticketHint: "A ticket number such as KHA-10001 shows the reply with that ticket's values.",
      unknown: "These variables do not exist and will be empty: {{variables}}",
    },
  },
  portal: {
    shownTitle: "What clients see",
    shownIntro: "Your clients follow their tickets in My tickets on the website, the web app and the mobile app. Choose what they see about each ticket.",
    priority: {
      label: "Show the priority to clients",
      hint: "Off: clients see the status only, never Urgent, High, Normal or Low.",
    },
    names: {
      label: "Show staff first names",
      hint: "Off: replies and assignments read as the team name, for example “Support team”.",
    },
    assignee: {
      label: "Show who the ticket is assigned to",
      hint: "Off: clients see replies but not who is handling the ticket.",
    },
    neverShown: "Clients never see internal notes, whether an SLA was breached, or ticket types marked back office.",
    saved: "Client portal settings saved.",
    preview: {
      label: "Client preview",
      title: "Client preview",
      heading: "My tickets",
      ticket: "KHA-10419 · Printer will not scan",
      sample: "Last update today",
      assigned: "Assigned to {{who}}",
      reply: "We have sent a technician's quote for you to confirm.",
      staffNamed: "Ana from Support team",
      team: "Support team",
    },
    behaviour: {
      title: "How the portal behaves",
      org: "Organisation admins also see every ticket of their organisation; other contacts see their own and the ones shared with them.",
      magic: "Clients can sign in with an e-mail link that is valid for {{minutes}} minutes and works once.",
      reopen: "A client can reopen a resolved ticket until it closes by itself, {{days}} days after it is resolved.",
      internal: "Tickets of back-office or tracker types and internal notes stay on the staff side.",
      language: "The assistant answers in the language the client writes in; the portal follows the client's language.",
    },
    share: {
      title: "Groups and branding",
      groups: "The groups clients see when they raise a ticket (Problems, Requests, Orders & billing, Feedback) are set per ticket type.",
      branding: "The portal uses your workspace name, logo and colour from the workspace settings.",
      typesLink: "Open ticket types",
      brandingLink: "Open workspace settings",
    },
  },
  notifications: {
    staff: {
      title: "Your notifications",
      caption: "Service notifications you receive, by channel",
      intro: "Choose which Service events you hear about and where. This applies to you in this workspace.",
      event: "Event",
      on: "On",
      saved: "Your Service notifications were saved.",
      failed: "We couldn't load your notification choices. Try again later.",
      emptyTitle: "No Service notifications to set",
      emptyBody: "They appear here once the Service Desk is switched on for your workspace.",
    },
    types: {
      ticket_new: { name: "A new ticket in my team", hint: "A ticket arrives in a team you belong to." },
      ticket_assigned: { name: "A ticket is assigned to me", hint: "Someone or a rule hands you a ticket." },
      ticket_client_replied: { name: "The client replied", hint: "A client answers on a ticket you work." },
      ticket_mention: { name: "I am mentioned in a note", hint: "A colleague @mentions you in an internal note." },
      ticket_sla: { name: "An SLA is close or missed", hint: "A target is about to be missed, or was." },
      ticket_csat_low: { name: "A low satisfaction rating", hint: "A client rates a ticket low." },
      ticket_returned_to_queue: { name: "A ticket returned to the queue", hint: "A member left and their tickets went back to the team." },
      order_placed: { name: "An order was placed", hint: "A client placed an order through the assistant." },
      order_issue: { name: "An order has a problem", hint: "A ticket was raised about an order." },
      order_sync_failed: { name: "An order could not be sent", hint: "The order system did not accept an order." },
      order_approval: { name: "An order waits for approval", hint: "An order needs a decision." },
      visit_scheduled: { name: "A visit was scheduled", hint: "A field visit was booked for you or your team." },
      visit_changed: { name: "A visit changed", hint: "A visit was moved or cancelled." },
      asset_due: { name: "Equipment service is due", hint: "Maintenance, calibration or warranty is coming due." },
      connector_health: { name: "A connector is unhealthy", hint: "A link to a client system stopped working." },
    },
    timers: {
      title: "Timers",
      intro: "When a ticket is escalated, a client is reminded, and a ticket resolves and closes by itself.",
      saved: "Timers saved.",
      unassigned: {
        label: "Escalate an unassigned ticket after (minutes)",
        hint: "Counted in office hours only.",
      },
      reminders: {
        label: "Remind a client who has not replied, after (days)",
        hint: "Up to {{max}} reminders, from {{from}} to {{to}} days after the ticket began waiting.",
        placeholder: "3",
      },
      autoResolve: {
        label: "Resolve a ticket the client never answers, after (days)",
        hint: "0 never resolves it by itself. It is resolved with the code “No response from client”.",
      },
      autoClose: {
        label: "Close a resolved ticket after (days)",
        hint: "A client can reopen it until then.",
      },
      problem: {
        number: "Enter a whole number.",
        range: "Use a number from {{min}} to {{max}}.",
        count: "Use up to 3 reminders.",
      },
    },
    digest: {
      title: "Outside office hours",
      staff: "Staff e-mail raised while the office is closed waits for one morning digest (up to {{lines}} lines) instead of sending at night. In-app notifications are not held back.",
      clients: "Clients are e-mailed at any hour. Several replies on one ticket within {{minutes}} minutes leave together in one e-mail.",
      hoursCalendar: "Office hours are the open hours of “{{name}}” ({{zone}}), the calendar of the default SLA policy.",
      hoursDefault: "No calendar is set on the default SLA policy, so office hours are Monday to Friday, 08:00 to 17:00.",
      hoursLink: "Open SLA & calendars",
    },
  },
  csat: {
    surveyTitle: "Satisfaction survey",
    surveyIntro: "When a ticket is resolved the client is asked to rate it from 1 to 5 stars.",
    stateOn: "Surveys are on",
    statePaused: "Surveys are paused",
    stateOff: "Surveys are off",
    enabled: {
      label: "Ask the requester for a rating when a ticket is resolved",
      hint: "Turn it off and nobody is asked; tickets are resolved as usual. Not sent for back-office or tracker tickets, nor for the resolutions listed below.",
    },
    pausedNote: "The workflow that asks for ratings is paused, so no survey is sent.",
    pausedLink: "Open the workflow",
    fatigue: {
      label: "Ask the same contact at most once every (days)",
      hint: "From {{min}} to {{max}}. 0 turns the cap off.",
      problem: {
        number: "Enter a whole number.",
        range: "Use a number from {{min}} to {{max}}.",
      },
    },
    alert: {
      label: "Alert the team lead at or below (stars)",
      hint: "From {{min}} to {{max}}. The team lead and the assignee are told about a rating this low.",
      problem: {
        number: "Enter a whole number.",
        range: "Use a number from {{min}} to {{max}}.",
      },
    },
    lowAlert: "A rating at or below this level alerts the team lead and the assignee.",
    recent: {
      title: "Recent ratings",
      ticket: "Ticket",
      contact: "Contact",
      score: "Score",
      reason: "Reason and comment",
      when: "When",
      outOf: "{{score}} out of 5",
      low: "Alerted",
      erased: "Erased contact",
      noComment: "No comment",
      emptyTitle: "No ratings yet",
      emptyBody: "Ratings appear here as clients answer the survey after a ticket is resolved.",
    },
    reasonsTitle: "Reasons for 1 to {{score}} stars",
    reasonsIntro: "A client who gives {{score}} stars or fewer picks one of these reasons and can add a comment.",
    reasonsEmptyTitle: "No reasons yet",
    reasonsEmptyBody: "Add the reasons clients can pick, such as “Took too long”.",
    reasonsList: "Reasons",
    addReason: "Add reason",
    reasonEn: "Reason {{n}} (English)",
    reasonFil: "Reason {{n}} (Filipino)",
    removeReason: "Remove reason {{name}}",
    reasonProblem: {
      required: "Write the reason in English.",
      long: "Use {{max}} characters or fewer.",
      duplicate: "This reason is already in the list.",
    },
    saved: "CSAT settings saved.",
    exclusionsTitle: "No survey when",
    exclusionsTypes: "Tickets of back-office and tracker types are never surveyed.",
    exclusionsList: "Resolution codes that skip the survey",
    skipped: "No survey",
    skipAria: "No survey when resolved as {{name}}",
    preview: {
      label: "Survey preview",
      title: "What the client sees",
      question: "How did we do on {{number}}?",
      stars: "Five stars to choose from",
      low: "With {{score}} stars or fewer the client picks a reason and can add a comment.",
    },
  },
} as const;
